import styled, { keyframes, css } from 'styled-components'
import colorsOptions from '../../fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions,
  textStyleSizeStyleOptions,
  textStyleWeightStyleOptions
} from '../../fundamentals/text-styles/constants'
import { listItemListViewVariantOptions } from './constants'

// Skeleton

const skeletonLoading = keyframes`
  0% {
    background-color: hsl(240, 11%, 85%);
  }
  100% {
    background-color: hsl(240, 11%, 95%);
  }
`

const skeletonSVGLoading = keyframes`
  0% {
    fill: hsl(240, 11%, 85%);
  }
  100% {
    fill: hsl(240, 11%, 95%);
  }
`

const SkeletonMask = styled.div`
  position: relative;
  width: 100px;
  height: 12px;
  border-radius: 15px;
  background-color: ${colorsOptions.greys['grey-20']};
  animation: ${skeletonLoading} 1s linear infinite alternate-reverse;

  ${({ skeletonStyle }) => skeletonStyle && css(skeletonStyle)}
`

const SkeletonSvg = styled.svg`
  path {
    animation: ${skeletonSVGLoading} 1s linear infinite alternate-reverse;
  }
`
// Component

const ListItemList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;

  [role='radiogroup'] {
    gap: 0.5rem;
  }

  form {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }

  ${({ listViewVariant }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
      border-radius: 16px;
      border: 1px solid ${colorsOptions.greys['grey-20']};
      background-color: ${colorsOptions.greys.white};
      gap: 0px;

      [role="radiogroup"] {
        gap: 0px;
      }

      form {
        gap: 0px;
      }
    `}
`

const ListItemContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${({ stateStyle }) => stateStyle.backgroundColor};

  ${({ listViewVariant, stateStyle }) =>
    listViewVariant === listItemListViewVariantOptions.SEPARATE &&
    `
      border: ${stateStyle.border};
      gap: 0.5rem;
      border-radius: 16px;
    `}

  ${({ inactive }) =>
    inactive &&
    `
      pointer-events: none;
    `}

    ${({ listViewVariant, firstItemKey, lastItemKey }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
          ${
            firstItemKey
              ? `
                border-top-left-radius: 16px;
                border-top-right-radius: 16px;
              `
              : ''
          }
          ${
            lastItemKey
              ? `
              border-bottom-left-radius: 16px; 
              border-bottom-right-radius: 16px;
            `
              : ''
          }
        `}
`

const ContainerContent = styled.div`
  display: flex;
  flex-direction: row;
  width: ${(props) => (props.withImage ? 'calc(100% - 120px)' : '100%')};
  height: 100%;
  padding: ${(props) => (props.noPadding ? '0' : '0.5rem 0')};

  ${({ listViewVariant, isLastItem, dividerFull, hideDivider }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
      padding-bottom: ${isLastItem ? '8px' : '0px'};
      ${!hideDivider && !isLastItem && dividerFull && `border-bottom: 1px solid ${colorsOptions.greys['grey-20']};`}
  `}
`

const ContainerContentLeft = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding-left: 16px;

  ${({ listViewVariant }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
      padding-bottom: 8px;
    `}
`

const LeftRadioContainer = styled.div`
  ${({ disable }) => disable && 'opacity: 0.5;'}
`

const ContainerContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: fit-content;
`

const ContainerContentListItem = styled(ContainerContentColumn)`
  width: 100%;
  display: flex;
  flex-grow: 1;
  gap: 0.5rem;
  padding-right: 8px;
`

const ListItemContainerTop = styled.div`
  width: 100%;
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const ContainerTopColumnFull = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 0.188rem;
  padding: 8px 8px 8px 16px;

  ${({ listViewVariant }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
      padding-left: 0px;
    `}
`

const ColumnFullFirtsLine = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const ColumnFullId = styled.span`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]};
`

const ColumnFullDate = styled.div`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]};
`

const ColumnFullTitle = styled.span`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  width: 100%;
`

const ColumnFullSecondaryText = styled.span`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-400']]};
`

const ColumnFullTertiaryText = styled(ColumnFullSecondaryText)``

const ContainerTopColumnBagdgeAndSwitch = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: fit-content;
  gap: 1rem;
  padding: 8px 16px 8px 8px;
`

const ContainerTopMetaData = styled.div`
  width: fit-content;
  white-space: nowrap;
  display: flex;
  padding: 8px 0px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  gap: 0.625rem;
`

const MetaText = styled.span`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]};
`

const Value = styled.span`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-R']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]};
`

const MetaRightBottom = styled.div`
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-S']]};
  color: ${({ colorTheme }) => colorTheme};
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]};
`

const ContentColumnIconConfirm = styled(ContainerContentColumn)`
  width: fit-content;
  padding: 0px 16px;
`

const ContentColumnActionRight = styled(ContainerContentColumn)`
  padding-right: 12px;
`

const ListItemContainerBottom = styled.div`
  width: 100%;
  display: flex;
  align-items: start;
  justify-content: center;

  ${({ listViewVariant }) =>
    listViewVariant === listItemListViewVariantOptions.SEPARATE &&
    `
      padding: 0px 8px 0px 16px;
    `}
`

const ContainerImage = styled.div`
  position: absolute;
  display: flex;
  flex-grow: 1;
  right: 0;
  overflow: hidden;
  z-index: 0;
  width: 120px;
  height: 100%;
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;

  ${({ listViewVariant, firstItemKey, lastItemKey }) => {
    if (listViewVariant === listItemListViewVariantOptions.TOGETHER) {
      return `
        ${firstItemKey ? 'border-radius: 0px 16px 0px 0px;' : ''}
        ${lastItemKey ? 'border-radius: 0px 0px 16px 0px;' : ''}
        border-bottom: 1px solid ${colorsOptions.greys['grey-20']};
      `
    } else if (listViewVariant === listItemListViewVariantOptions.SEPARATE) {
      return 'border-radius: 0px 16px 16px 0px;'
    }
    return 'border-radius: 0px'
  }}

  ${({ disable }) =>
    disable &&
    `
      filter: grayscale(100%);
      opacity: 0.5;
    `}
`

const DividerItens = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;

  ${({ listViewVariant, isLastItem, dividerFull, hideDivider }) =>
    listViewVariant === listItemListViewVariantOptions.TOGETHER &&
    `
      margin-left: 16px;
      padding-bottom: 8px;
      ${!hideDivider && !isLastItem && dividerFull && `border-bottom: 1px solid ${colorsOptions.greys['grey-20']};`}
  `}
`

// Storybook

const ContainerMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`

const MetaLine = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`

const LineItem = styled.span`
  color: ${colorsOptions.greys['grey-70']};
`

export {
  ListItemList,
  ListItemContainer,
  ContainerContent,
  ContainerContentLeft,
  LeftRadioContainer,
  ContainerContentColumn,
  ContainerContentListItem,
  ListItemContainerTop,
  ContainerTopColumnFull,
  ColumnFullFirtsLine,
  ColumnFullId,
  ColumnFullDate,
  ColumnFullTitle,
  ColumnFullSecondaryText,
  ColumnFullTertiaryText,
  ContainerTopColumnBagdgeAndSwitch,
  ContainerTopMetaData,
  MetaText,
  Value,
  MetaRightBottom,
  ContentColumnIconConfirm,
  ContentColumnActionRight,
  ListItemContainerBottom,
  ContainerImage,
  DividerItens,
  SkeletonMask,
  SkeletonSvg,
  ContainerMeta,
  MetaLine,
  LineItem
}
